export default class BinancePrivateClient {
  constructor({ apiKey, secret, baseURL = "https://fapi.binance.com", recvWindow = 5000 }) {
    this.apiKey = apiKey
    this.secret = secret
    this.baseURL = baseURL
    this.recvWindow = recvWindow
    this.encoder = new TextEncoder()
    this.timeOffset = 0
    this.timeSyncedAt = 0
    this.timeSyncTTL = 30000
    this.timeSyncPromise = null
  }

  // ========= 工具 =========
  async _sign(queryString) {
    const key = await crypto.subtle.importKey(
      "raw",
      this.encoder.encode(this.secret),
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["sign"]
    )

    const signature = await crypto.subtle.sign(
      "HMAC",
      key,
      this.encoder.encode(queryString)
    )

    return Array.from(new Uint8Array(signature))
      .map(b => b.toString(16).padStart(2, "0"))
      .join("")
  }

  _buildQuery(params = {}) {
    return new URLSearchParams(params).toString()
  }

  _isTimeSynced() {
    return this.timeSyncedAt && Date.now() - this.timeSyncedAt < this.timeSyncTTL
  }

  async syncServerTime(force = false) {
    if (!force && this._isTimeSynced()) return this.timeOffset

    if (this.timeSyncPromise) return this.timeSyncPromise

    this.timeSyncPromise = this._fetchServerTimeOffset()
      .finally(() => {
        this.timeSyncPromise = null
      })

    return this.timeSyncPromise
  }

  async _fetchServerTimeOffset() {
    const startedAt = Date.now()
    const response = await fetch(this.baseURL + "/fapi/v1/time")
    const endedAt = Date.now()
    const data = await response.json()

    if (!response.ok || typeof data.serverTime === "undefined") {
      const error = new Error(data.msg || "Failed to sync Binance server time")
      error.data = data
      throw error
    }

    const localMidpoint = Math.floor((startedAt + endedAt) / 2)
    this.timeOffset = Number(data.serverTime) - localMidpoint
    this.timeSyncedAt = endedAt

    return this.timeOffset
  }

  _timestamp() {
    return Date.now() + this.timeOffset
  }

  async _request(path, method = "GET", params = {}, signed = false, retryOnTimeError = true) {
    const requestParams = { ...params }

    if (signed) {
      await this.syncServerTime()

      if (typeof requestParams.recvWindow === "undefined") {
        requestParams.recvWindow = this.recvWindow
      }

      //requestParams.timestamp = this._timestamp()Date.now()
      requestParams.timestamp = Date.now()
    }

    let query = this._buildQuery(requestParams)

    if (signed) {
      const signature = await this._sign(query)
      query += "&signature=" + signature
    }

    const url = this.baseURL + path + (query ? "?" + query : "")

    try {
      const response = await fetch(url, {
        method,
        headers: {
          "X-MBX-APIKEY": this.apiKey
        }
      })

      const data = await response.json()

      // ✅ 关键：优先判断 Binance 业务错误
      if (typeof data.code !== "undefined" && data.code < 0) {
        const error = new Error(data.msg || `API Error ${data.code}`)
        error.code = data.code
        error.data = data
        throw error
      }

      // ⚠️ HTTP 错误只作为补充（但不绝对）
      if (!response.ok) {
        console.warn("HTTP not OK but Binance returned:", data)
      }

      return data

    } catch (error) {
      if (signed && retryOnTimeError && error.code === -1021) {
        await this.syncServerTime(true)
        return this._request(path, method, params, signed, false)
      }

      console.error("Request failed:", error.message || error)
      throw error // ✅ 保留原始错误，不要重新包装
    }
  }

  // ========= 账户 =========

  // 获取 listenKey
  getListenKey() {
    return this._request("/fapi/v1/listenKey", "POST")
  }

  async keepAliveListenKey() {
    return await this._request("/fapi/v1/listenKey", "PUT")
  }

  // 获取账户信息 / 余额
  accountInfo() {
    return this._request("/fapi/v2/account", "GET", {}, true)
  }

  // 获取全部持仓（含杠杆、保证金模式）
  positionsV2() {
    return this._request("/fapi/v2/positionRisk", "GET", {}, true)
  }

  // ========= 交易 =========

  // 下单
  order(params) {
    return this._request("/fapi/v1/order", "POST", params, true)
  }

  // 查询挂单
  openOrder(symbol) {
    return this._request("/fapi/v1/openOrders", "GET", { symbol }, true)
  }

  // 查询挂单
  openOrders() {
    return this._request("/fapi/v1/openOrders", "GET", {}, true)
  }

  // 撤单
  cancelOrder(symbol, orderId) {
    return this._request("/fapi/v1/order", "DELETE", { symbol, orderId }, true)
  }

  // 批量撤单
  cancelOrders(symbol) {
    return this._request("/fapi/v1/allOpenOrders", "DELETE", { symbol }, true)
  }

  //下条件订单
  algoOrder(params) {
    return this._request("/fapi/v1/algoOrder", "POST", params, true)
  }

  // 查询条件订单
  openAlgoOrders() {
    return this._request("/fapi/v1/openAlgoOrders", "GET", {}, true)
  }

  //撤销条件订单
  cancelAlgoOrder(algoId) {
    return this._request("/fapi/v1/algoOrder", "DELETE", { algoId }, true)
  }

  // 批量撤条件订单
  cancelAlgoOrders(symbol) {
    return this._request("/fapi/v1/algoOpenOrders", "DELETE", { symbol }, true)
  }

  // ========= 账户设置 =========

  // 修改持仓模式
  setMarginType(symbol, marginType) {
    return this._request("/fapi/v1/marginType", "POST", { symbol, marginType }, true)
  }

  // 设置杠杆
  setLeverage(symbol, leverage) {
    return this._request("/fapi/v1/leverage", "POST", { symbol, leverage }, true)
  }

  // ========= 成交 =========

  // 成交历史
  myTrades(params) {
    return this._request("/fapi/v1/userTrades", "GET", params, true
    )
  }
}
